"use client"

import { notify } from "@/lib/notifications/notify"
import type React from "react"
import { useState, useEffect, useRef, useLayoutEffect } from "react"
import { AlertCircle, Check, Loader2 } from "lucide-react"
import { Textarea } from "@/components/ui/textarea"
import { BoothMark } from "@/components/ui/booth-mark"
import { SendMark } from "@/components/ui/send-mark"
import { HeroCtaButton } from "@/components/glass/hero-cta"
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetDescription,
  BottomSheetTitle,
} from "@/components/ui/bottom-sheet"
import { createClient } from "@/lib/supabase/client"
import { useRouter } from "next/navigation"
import { usePostHog } from "posthog-js/react"
import Link from "next/link"

// Contact the shop about a chair. Same sheet as the hiring inquiry
// (JobInquireModal) — the Apple Pay shape, rising from the edge the thumb is
// already at, widening and stepping up its type from sm. The two are the same
// act from the barber's side, and they used to look like two different
// products: this one was a centred dialog with a Cancel button.
//
// Signed out, the sheet still rises; it just asks them to make an account
// first instead of pretending the message will send.

interface ContactOwnerModalProps {
  isOpen: boolean
  onClose: () => void
  shopId: string
  shopName: string
  ownerId: string
}

export function ContactOwnerModal({ isOpen, onClose, shopId, shopName, ownerId }: ContactOwnerModalProps) {
  const [message, setMessage] = useState("")
  const [loading, setLoading] = useState(false)
  // The message box sizes to its content: tall enough to show the whole
  // placeholder, then grows line by line as the barber types (up to a cap,
  // after which it scrolls). A fixed rows= count either cut the prompt off
  // on phones or left a large empty box on desktop.
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const MESSAGE_MIN = 168
  const MESSAGE_MAX = 360
  useLayoutEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = "auto"
    el.style.height = Math.min(MESSAGE_MAX, Math.max(MESSAGE_MIN, el.scrollHeight)) + "px"
  }, [message, isOpen])
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const router = useRouter()
  const posthog = usePostHog()

  useEffect(() => {
    if (!isOpen) return

    const checkAuth = async () => {
      const supabase = createClient()
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setIsAuthenticated(!!user)
    }
    checkAuth()
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setMessage("")
      setSuccess(false)
      setError(null)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!message.trim()) return

    setLoading(true)
    setError(null)
    const supabase = createClient()

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser()

      if (!user) {
        router.push("/auth/login")
        return
      }

      if (!ownerId) {
        setError("Unable to contact shop owner. Please try again later.")
        return
      }

      if (user.id === ownerId) {
        setError("You cannot send a message to yourself.")
        return
      }

      const { data: senderProfile } = await supabase.from("profiles").select("full_name").eq("id", user.id).single()

      const { data: existingConv } = await supabase
        .from("conversations")
        .select("id")
        .or(
          `and(participant_1.eq.${user.id},participant_2.eq.${ownerId}),and(participant_1.eq.${ownerId},participant_2.eq.${user.id})`,
        )
        .maybeSingle()

      let conversationId = existingConv?.id

      if (!conversationId) {
        const { data: newConv, error: convError } = await supabase
          .from("conversations")
          .insert({
            participant_1: user.id,
            participant_2: ownerId,
            last_message_at: new Date().toISOString(),
          })
          .select("id")
          .single()

        if (convError) {
          throw convError
        }
        conversationId = newConv.id
      }

      const { error: msgError } = await supabase.from("messages").insert({
        conversation_id: conversationId,
        sender_id: user.id,
        receiver_id: ownerId,
        content: `Re: ${shopName}\n\n${message.trim()}`,
        read: false,
      })

      if (msgError) {
        throw msgError
      }

      await supabase
        .from("conversations")
        .update({ last_message_at: new Date().toISOString() })
        .eq("id", conversationId)

      await notify({
        recipient: ownerId,
        type: "new_message",
        shopId,
        metadata: {
          conversation_id: conversationId,
          shop_id: shopId,
          shop_name: shopName,
        },
      })

      // Lead generated — the bottom of the marketplace contact funnel.
      posthog?.capture("contact_owner_sent", {
        shop_id: shopId,
        shop_name: shopName,
        $groups: { shop: shopId },
      })

      setSuccess(true)
      setTimeout(() => {
        router.push(`/dashboard/messages?conversation=${conversationId}`)
      }, 2500)
    } catch (error) {
      console.error("Error sending message:", error)
      setError("Failed to send message. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const signedOut = isAuthenticated === false

  return (
    <BottomSheet open={isOpen} onOpenChange={(o) => { if (!o && !loading) onClose() }}>
      <BottomSheetContent closeDisabled={loading} className="sm:max-w-[576px]">
        {success ? (
          /* Confirmation in place, same height band — nothing jumps. */
          <div className="flex flex-col items-center gap-3 px-6 pb-10 pt-10 text-center sm:px-8 sm:pb-12 sm:pt-12">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30">
              <Check className="h-6 w-6 text-emerald-400" strokeWidth={2} />
            </span>
            <BottomSheetTitle className="text-base font-medium">Message sent</BottomSheetTitle>
            <BottomSheetDescription className="max-w-[34ch] text-sm leading-relaxed text-white/50">
              {shopName} can reply in your messages. Taking you there…
            </BottomSheetDescription>
            <Link
              href="/dashboard/messages"
              className="mt-1 text-[13px] font-semibold text-white underline-offset-4 hover:underline"
            >
              Go to messages now
            </Link>
          </div>
        ) : (
          <>
            <div className="px-6 pb-5 pt-3 sm:px-8 sm:pb-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/40">
                {signedOut ? "Contact" : "Contact shop"}
              </p>
              <div className="mt-1.5 flex items-center gap-2.5">
                <BoothMark size={26} className="shrink-0 text-white/45" />
                <BottomSheetTitle className="text-xl font-medium leading-snug tracking-tight sm:text-[22px]">
                  {shopName}
                </BottomSheetTitle>
              </div>
              <BottomSheetDescription className="mt-1 text-sm text-white/50">
                {signedOut
                  ? "Messages go to the shop through your BOOTH account."
                  : "Ask about a chair, their rates, or when you could start."}
              </BottomSheetDescription>
            </div>

            {signedOut ? (
              /* The account gate. Same sheet, same CTA — only the ask changes. */
              <div className="px-6 pb-6 pt-1 sm:px-8 sm:pb-8">
                <HeroCtaButton
                  onClick={() => router.push(`/auth/signup?redirect=/listings/${shopId}`)}
                  invert={{ bg: "#FFFFFF", fg: "#0C0C0C" }}
                  className="sm:h-[52px] sm:text-base"
                >
                  Create your account
                </HeroCtaButton>
                <p className="mt-3 text-center text-[13px] text-white/50">
                  Already on BOOTH?{" "}
                  <Link
                    href={`/auth/login?redirect=/listings/${shopId}`}
                    className="font-semibold text-white hover:underline"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="px-6 sm:px-8">
                  <label htmlFor="contact-shop-message" className="sr-only">
                    Your message
                  </label>
                  <Textarea
                    id="contact-shop-message"
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={1}
                    maxLength={1000}
                    disabled={loading}
                    style={{ minHeight: MESSAGE_MIN, maxHeight: MESSAGE_MAX, overflowY: "auto" }}
                    className="resize-none rounded-2xl border-white/10 bg-white/[0.04] text-sm leading-relaxed text-white placeholder:text-white/35 focus-visible:border-white/25 focus-visible:ring-1 focus-visible:ring-white/20"
                    placeholder="Hi, I'm interested in renting a chair. Could you tell me about availability and rates?"
                    required
                  />
                  <p className="mt-2 text-right text-[11px] tabular-nums text-white/30">{message.length}/1000</p>
                  {error && (
                    <p className="mt-2 flex items-start gap-1.5 text-[12px] leading-relaxed text-red-400">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      {error}
                    </p>
                  )}
                </div>

                {/* One commit at the bottom edge. Cancel is the X and the
                    backdrop; a second button here would only compete. */}
                <div className="px-6 pb-6 pt-4 sm:px-8 sm:pb-8">
                  <HeroCtaButton
                    type="submit"
                    disabled={loading || !message.trim()}
                    invert={{ bg: "#FFFFFF", fg: "#0C0C0C" }}
                    className="sm:h-[52px] sm:text-base"
                  >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendMark size={16} />}
                    Send message
                  </HeroCtaButton>
                </div>
              </form>
            )}
          </>
        )}
      </BottomSheetContent>
    </BottomSheet>
  )
}
